import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCardIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { getWorkspaceBillingStatus } from '../../services/billingApi';

function getInitials(first, last, email) {
  const a = (first || '').trim()[0];
  const b = (last || '').trim()[0];
  if (a || b) return `${a || ''}${b || ''}`.toUpperCase();
  return (email || '?').trim()[0]?.toUpperCase() || '?';
}

export default function HeaderAccountMenu({
  username,
  lastName,
  workspaceName,
  userNavigation,
  onSignOut,
  variant = 'header'
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [planLabel, setPlanLabel] = useState('');
  const rootRef = useRef(null);

  const isSidebar = variant === 'sidebar';
  const displayName = `${username || ''} ${lastName || ''}`.trim() || 'Account';
  const displayWorkspaceName = workspaceName || 'My Workspace';

  useEffect(() => {
    const storedAvatarUrl = localStorage.getItem('user_avatar');
    if (storedAvatarUrl) {
      setAvatarUrl(storedAvatarUrl);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const workspaceId = localStorage.getItem('workspace_id');
    if (!workspaceId) return () => {};

    getWorkspaceBillingStatus(workspaceId)
      .then((result) => {
        if (!mounted) return;
        const planCode = result?.subscription?.plan_code
          || result?.subscription?.plan
          || result?.subscription?.plan_name
          || '';
        const status = String(result?.subscription?.status || '').toLowerCase();

        if (planCode) {
          setPlanLabel(`(${planCode})`);
        } else if (status === 'trialing' || status === 'trial') {
          setPlanLabel('(trial)');
        } else {
          setPlanLabel('');
        }
      })
      .catch(() => {
        // billing is optional
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const handleSignOut = () => {
    setOpen(false);
    if (onSignOut) {
      onSignOut();
    } else if (userNavigation && userNavigation.length > 0) {
      const signOutItem = userNavigation.find((item) => item.name?.toLowerCase().includes('sign out') || item.name?.toLowerCase().includes('logout'));
      if (signOutItem && signOutItem.onClick) {
        signOutItem.onClick();
      }
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          isSidebar
            ? 'w-11 h-11 rounded-xl bg-gray-100 border border-gray-200 hover:bg-gray-200 transition-all flex items-center justify-center overflow-hidden'
            : 'h-8 pl-2 pr-3 rounded-full bg-gray-100 border border-gray-200 hover:bg-gray-200 transition-all flex items-center gap-2'
        }
        title={`${displayName} - ${displayWorkspaceName}`}
        aria-label={displayName}
      >
        <div className={`${isSidebar ? 'w-8 h-8' : 'w-6 h-6'} rounded-full bg-primary-500 text-white flex items-center justify-center overflow-hidden text-[11px] font-bold`}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
          ) : (
            <span>{getInitials(username, lastName)}</span>
          )}
        </div>
        {!isSidebar && (
          <div className="flex flex-col items-start">
            <span className="text-xs text-gray-900 max-w-[140px] truncate leading-tight">{displayName}</span>
            <span className="text-[10px] text-gray-600 max-w-[140px] truncate leading-tight">
              {displayWorkspaceName}
              {planLabel ? ` ${planLabel}` : ''}
            </span>
          </div>
        )}
      </button>

      {open && (
        <div className={`absolute w-48 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden z-[200] ${
          isSidebar ? 'left-full bottom-0 ml-2' : 'right-0 mt-2'
        }`}
        >
          <button
            onClick={() => {
              setOpen(false);
              navigate('/profile#subscription');
            }}
            className="w-full px-3 py-2.5 flex items-center gap-2 text-sm text-gray-900 hover:bg-gray-50 transition-colors"
          >
            <CreditCardIcon className="w-4 h-4" />
            Billing
          </button>
          <button
            onClick={() => {
              setOpen(false);
              navigate('/profile');
            }}
            className="w-full px-3 py-2.5 flex items-center gap-2 text-sm text-gray-900 hover:bg-gray-50 transition-colors"
          >
            <Cog6ToothIcon className="w-4 h-4" />
            Settings
          </button>
          <button
            onClick={handleSignOut}
            className="w-full px-3 py-2.5 flex items-center gap-2 text-sm text-gray-900 hover:bg-gray-50 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4" />
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
